import React, { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  BodyText,
  Button,
  DisplayText,
  HStack,
  Image,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import type { DietaryPropertyKind } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { IllusDietaryRestrictions } from './assets';
import { useDietaryRestrictionsMachine } from './dietaryRestrictionsMachine';

// ────────────────────────────────────────────────────────────────────────────────

export const DietaryRestrictions = (props: DietaryRestrictionsProps) => {
  const { title, shouldHideIllustration } = props;
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const { sortedRestrictionsNames, restrictions } =
    useDietaryRestrictionsMachine();

  const descriptionSize = currentBreakpoint.isMobile ? 4 : 3;

  // ─────────────────────────────────────────────────────────────────

  return (
    <View
      testID="dietary-restrictions.container"
      style={styles.contentContainer}
    >
      {shouldHideIllustration ? null : (
        <View style={styles.illustrationWrapper}>
          <Image
            testID="dietary-restrictions.illustration"
            source={IllusDietaryRestrictions}
            style={styles.illustration}
            accessibilityIgnoresInvertColors
            resizeMode="contain"
          />
        </View>
      )}

      {title ? (
        <DietaryRestrictionsTitle>{title}</DietaryRestrictionsTitle>
      ) : null}

      <BodyText size={descriptionSize}>
        {t('dietary-restrictions.description-start')}
        <Text> </Text>
        <Text style={styles.descriptionHighlight}>
          {t('dietary-restrictions.description-highlight')}
        </Text>
        <Text>{t('dietary-restrictions.description-end')}</Text>
      </BodyText>

      <View style={styles.togglesWrapper}>
        <HStack
          itemsPerRow={3}
          style={styles.togglesStack}
          customSpacing={theme.spacing.medium}
        >
          {sortedRestrictionsNames.map((restriction) => (
            <DietaryRestrictionToggle
              key={restriction}
              restriction={restriction}
              checked={restrictions[restriction]}
            />
          ))}
        </HStack>
      </View>

      <View>
        <View style={styles.noticeWrapper}>
          <BodyText size={5}>{t('dietary-restrictions.notice-1')}</BodyText>
        </View>

        <View>
          <BodyText size={5}>{t('dietary-restrictions.notice-2')}</BodyText>
        </View>
      </View>
    </View>
  );
};
// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const DietaryRestrictionsTitle: React.FC = (props) => {
  const { currentBreakpoint } = useResponsive();

  const titleSize = currentBreakpoint.isMobile ? 4 : 3;

  return (
    <View style={styles.titleWrapper}>
      <DisplayText size={titleSize}>{props.children}</DisplayText>
    </View>
  );
};

const DietaryRestrictionToggle = (props: DietaryRestrictionsToggleProps) => {
  const { t } = useLocalizationContext();
  const { toggleRestriction } = useDietaryRestrictionsMachine();
  const { restriction, checked } = props;

  const formattedRestrictionName = restriction.toLowerCase();

  // ─── HELPERS ──────────────────────────────────────────────────────────

  const onPress = useCallback(() => {
    toggleRestriction(restriction);
  }, [toggleRestriction, restriction]);

  // ────────────────────────────────────────────────────────────────────

  const accessibilityLabel = t('dietary-restrictions.toggle-restriction', {
    restriction: formattedRestrictionName,
  });

  const togglePalette = checked ? 'primaryOutline' : 'muted';

  return (
    <Button
      testID={`dietary.restriction.toggle.${formattedRestrictionName}`}
      palette={togglePalette}
      onPress={onPress}
      focusOutlineColor={theme.colors.GRAY}
      style={styles.toggle}
      size="large"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ checked }}
      accessibilityRole="checkbox"
    >
      <Text style={styles.toggleText} numberOfLines={1}>
        {formattedRestrictionName}
      </Text>
    </Button>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {
    alignSelf: 'center',
    alignItems: 'center',
    maxWidth: '100%',
  },
  illustrationWrapper: {
    paddingBottom: theme.spacing.large,
  },
  illustration: {
    width: 232,
    height: 124,
  },
  titleWrapper: {
    paddingBottom: theme.spacing.large,
  },
  descriptionHighlight: {
    color: theme.colors.CAUTION,
  },
  togglesWrapper: {
    width: '100%',
    paddingVertical: theme.spacing.large,
  },
  togglesStack: {
    paddingVertical: theme.spacing.large,
  },
  toggle: {
    paddingHorizontal: theme.spacing.xsmall,
  },
  noticeWrapper: {
    paddingBottom: theme.spacing.medium,
  },
  toggleText: {
    textTransform: 'capitalize',
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type DietaryRestrictionsProps = {
  readonly shouldHideIllustration?: boolean;
  readonly title?: string;
};

type DietaryRestrictionsToggleProps = {
  readonly restriction: DietaryPropertyKind;
  readonly checked: boolean;
};
